import React, { useState, useEffect } from 'react';
import { Play, AlertCircle, CheckCircle, Loader, Terminal, MessageSquare, BookOpen } from 'lucide-react';
import SimulationEngine from './SimulationEngine';
import { simulations } from '../data/simulations';

/**
 * Live Demo Tab Component
 * 
 * Two modes:
 * 1. Interactive Simulations - Step-by-step A2A message visualization
 * 2. Live Backend Demos - Real API calls to the backend
 */
const LiveDemoTab = ({ a2aClient, isRunning, setIsRunning, currentStep, setCurrentStep }) => {
  const [mode, setMode] = useState('simulation'); // 'simulation' or 'live'
  const [selectedDemo, setSelectedDemo] = useState('content-generation');
  const [selectedSimulation, setSelectedSimulation] = useState('capability_discovery');
  const [demoState, setDemoState] = useState('ready');
  const [logs, setLogs] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const demos = {
    'content-generation': {
      name: 'Content Generation Demo',
      description: 'Generate AI-powered educational content in real-time',
      icon: '📚',
      capability: 'generate_learning_content',
      parameters: {
        topic: 'capability_discovery',
        difficulty: 'beginner',
        include_simulation: true
      },
      steps: [
        'Discovering backend capabilities',
        'Sending content generation request',
        'AI is creating educational content',
        'Backend saves content to database',
        'Receiving generated module'
      ]
    },
    'simulation': {
      name: 'Interactive Simulation',
      description: 'Run a live A2A interaction simulation',
      icon: '🎮',
      capability: 'simulate_a2a_interaction',
      parameters: {
        simulation_type: 'capability_discovery',
        complexity: 'basic'
      },
      steps: [
        'Setting up simulation environment',
        'Creating virtual agents',
        'Running capability discovery',
        'Evaluating performance',
        'Generating feedback'
      ]
    },
    'assessment': {
      name: 'Quiz Assessment',
      description: 'Take a quiz and get AI-powered feedback',
      icon: '📝',
      capability: 'assess_understanding',
      parameters: {
        topic: 'capability_discovery',
        answers: [0, 2, 1, 3]
      },
      steps: [
        'Submitting quiz answers',
        'AI evaluating responses',
        'Calculating score',
        'Generating personalized feedback',
        'Updating progress'
      ]
    }
  };

  const currentDemo = demos[selectedDemo];

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const runDemo = async () => {
    if (!a2aClient) {
      addLog('❌ A2A Client not initialized. Please refresh the page.', 'error');
      return;
    }

    setIsRunning(true);
    setDemoState('running');
    setCurrentStep(0);
    setLogs([]);
    setResult(null);
    setError(null);

    try {
      // Step through each demo step with delays for visualization
      for (let i = 0; i < currentDemo.steps.length; i++) {
        setCurrentStep(i);
        addLog(`⏳ ${currentDemo.steps[i]}...`, 'info');
        
        // Simulate step delay
        await new Promise(resolve => setTimeout(resolve, i === 0 ? 500 : 1000));
        
        // Execute actual task on the main step
        if (i === Math.floor(currentDemo.steps.length / 2)) {
          addLog(`🚀 Executing ${currentDemo.capability}`, 'info');
          
          const response = await a2aClient.executeTask(
            currentDemo.capability,
            currentDemo.parameters
          );

          if (response.success) {
            addLog(`✅ ${currentDemo.steps[i]} - Success!`, 'success');
            setResult(response);
          } else {
            throw new Error(response.error || 'Task execution failed');
          }
        } else {
          addLog(`✅ ${currentDemo.steps[i]} - Complete`, 'success');
        }
      }

      setCurrentStep(currentDemo.steps.length);
      setDemoState('success');
      addLog('🎉 Demo completed successfully!', 'success');

    } catch (err) {
      console.error('Demo error:', err);
      setError(err.message);
      setDemoState('error');
      addLog(`❌ Error: ${err.message}`, 'error');
    } finally {
      setIsRunning(false);
    }
  };

  const resetDemo = () => {
    setDemoState('ready');
    setCurrentStep(0);
    setLogs([]);
    setResult(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Mode Selector */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Interactive Demos</h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose between simulations or live backend demos
            </p>
          </div>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setMode('simulation')}
              className={`px-6 py-2 rounded-lg font-medium text-sm transition-all ${
                mode === 'simulation'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <BookOpen className="w-4 h-4 inline mr-2" />
              Simulations
            </button>
            <button
              onClick={() => setMode('live')}
              className={`px-6 py-2 rounded-lg font-medium text-sm transition-all ${
                mode === 'live'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Terminal className="w-4 h-4 inline mr-2" />
              Live Backend
            </button>
          </div>
        </div>
      </div>

      {/* Simulation Mode */}
      {mode === 'simulation' && (
        <div>
          <div className="grid grid-cols-3 gap-4 mb-6">
            {Object.entries(simulations).map(([key, sim]) => (
              <button
                key={key}
                onClick={() => setSelectedSimulation(key)}
                className={`card p-4 text-left transition-all hover:shadow-lg ${
                  selectedSimulation === key
                    ? 'ring-2 ring-indigo-600 bg-indigo-50'
                    : 'hover:border-indigo-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-gray-900">{sim.name}</h3>
                  <span className={`badge text-xs ${
                    sim.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                    sim.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {sim.difficulty}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{sim.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{sim.messages.length} steps</span>
                  <span>{sim.duration}</span>
                </div>
              </button>
            ))}
          </div>

          <SimulationEngine 
            simulation={simulations[selectedSimulation]}
            onComplete={() => {
              // Auto-advance to next simulation or show completion
              const simKeys = Object.keys(simulations);
              const currentIndex = simKeys.indexOf(selectedSimulation);
              if (currentIndex < simKeys.length - 1) {
                setTimeout(() => {
                  setSelectedSimulation(simKeys[currentIndex + 1]);
                }, 2000);
              }
            }}
          />
        </div>
      )}

      {/* Live Backend Mode */}
      {mode === 'live' && (
        <div className="grid grid-cols-12 gap-6">
          {/* Demo Selection */}
          <div className="col-span-4">
            <div className="card p-4 sticky top-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Choose a Demo</h2>
          <div className="space-y-3">
            {Object.entries(demos).map(([key, demo]) => (
              <button
                key={key}
                onClick={() => {
                  setSelectedDemo(key);
                  resetDemo();
                }}
                disabled={isRunning}
                className={`w-full text-left p-4 rounded-lg transition-all ${
                  selectedDemo === key
                    ? 'bg-indigo-50 border-2 border-indigo-600'
                    : 'bg-white border border-gray-200 hover:border-indigo-300'
                } ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-start space-x-3">
                  <span className="text-3xl">{demo.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 text-sm mb-1">
                      {demo.name}
                    </div>
                    <div className="text-xs text-gray-600">
                      {demo.description}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Connection Status */}
          <div className="mt-6 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${a2aClient ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
              <span className="text-xs text-gray-600">
                {a2aClient ? 'Backend Connected' : 'Backend Disconnected'}
              </span>
            </div>
          </div>

          {/* Run Button */}
          <button
            onClick={runDemo}
            disabled={!a2aClient || isRunning}
            className="w-full mt-4 btn-primary py-3 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Running...</span>
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                <span>Run Demo</span>
              </>
            )}
          </button>

          {demoState === 'success' && (
            <button
              onClick={resetDemo}
              className="w-full mt-2 btn-secondary py-3"
            >
              Reset & Try Again
            </button>
          )}
        </div>
      </div>

      {/* Demo Output */}
      <div className="col-span-8 space-y-4">
        {/* Demo Header */}
        <div className="card p-6">
          <div className="flex items-start space-x-4">
            <span className="text-4xl">{currentDemo.icon}</span>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">{currentDemo.name}</h2>
              <p className="text-gray-600 mt-1">{currentDemo.description}</p>
            </div>
            {demoState === 'success' && (
              <CheckCircle className="w-8 h-8 text-green-500" />
            )}
            {demoState === 'error' && (
              <AlertCircle className="w-8 h-8 text-red-500" />
            )}
          </div>

          {/* Progress Indicator */}
          {isRunning && (
            <div className="mt-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progress</span>
                <span>{currentStep + 1} / {currentDemo.steps.length}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${((currentStep + 1) / currentDemo.steps.length) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Console Logs */}
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Terminal className="w-5 h-5 text-gray-600" />
            <h3 className="font-bold text-gray-900">Console Output</h3>
          </div>
          
          <div className="bg-gray-900 rounded-lg p-4 h-64 overflow-y-auto font-mono text-sm">
            {logs.length === 0 ? (
              <div className="text-gray-500 italic">
                Console output will appear here when you run the demo...
              </div>
            ) : (
              <div className="space-y-1">
                {logs.map((log, idx) => (
                  <div 
                    key={idx} 
                    className={`animate-fadeIn ${
                      log.type === 'error' ? 'text-red-400' :
                      log.type === 'success' ? 'text-green-400' :
                      'text-gray-300'
                    }`}
                  >
                    <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Results Display */}
        {result && (
          <div className="card p-6 bg-green-50 border-green-200 animate-fadeIn">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg mb-2">Demo Results</h3>
                
                {/* Content Generation Results */}
                {selectedDemo === 'content-generation' && result.content && (
                  <div className="space-y-3">
                    <div>
                      <span className="font-medium">Module ID:</span>{' '}
                      <code className="bg-white px-2 py-1 rounded text-sm">{result.module_id}</code>
                    </div>
                    <div>
                      <span className="font-medium">Title:</span>{' '}
                      <span className="text-gray-700">{result.content.title}</span>
                    </div>
                    <div>
                      <span className="font-medium">Sections:</span>{' '}
                      <span className="text-gray-700">{result.content.sections?.length || 0} sections generated</span>
                    </div>
                    <div>
                      <span className="font-medium">Quiz Questions:</span>{' '}
                      <span className="text-gray-700">{result.content.quiz?.length || 0} questions created</span>
                    </div>
                  </div>
                )}

                {/* Simulation Results */}
                {selectedDemo === 'simulation' && result.results && (
                  <div className="space-y-3">
                    <div>
                      <span className="font-medium">Success:</span>{' '}
                      <span className={result.results.success ? 'text-green-700' : 'text-red-700'}>
                        {result.results.success ? '✅ Yes' : '❌ No'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Rating:</span>{' '}
                      <span className="text-yellow-600">
                        {'⭐'.repeat(result.results.rating || 0)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Feedback:</span>
                      <p className="text-gray-700 mt-1 italic">"{result.results.feedback}"</p>
                    </div>
                  </div>
                )}

                {/* Assessment Results */}
                {selectedDemo === 'assessment' && result.score !== undefined && (
                  <div className="space-y-3">
                    <div>
                      <span className="font-medium">Score:</span>{' '}
                      <span className="text-2xl font-bold text-indigo-600">{result.score}%</span>
                    </div>
                    <div>
                      <span className="font-medium">Feedback:</span>
                      <p className="text-gray-700 mt-1 italic">"{result.feedback}"</p>
                    </div>
                    {result.recommendations && (
                      <div>
                        <span className="font-medium">Next Steps:</span>
                        <ul className="list-disc list-inside text-gray-700 mt-1">
                          {result.recommendations.map((rec, idx) => (
                            <li key={idx}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Raw JSON Response */}
                <details className="mt-4">
                  <summary className="cursor-pointer font-medium text-sm text-gray-600 hover:text-gray-900">
                    View Raw Response
                  </summary>
                  <div className="mt-2 bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-xs text-gray-100 font-mono">
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  </div>
                </details>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="card p-6 bg-red-50 border-red-200 animate-fadeIn">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-red-900 text-lg">Demo Failed</h3>
                <p className="text-red-700 mt-1">{error}</p>
                <button
                  onClick={resetDemo}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        {demoState === 'ready' && (
          <div className="card p-6 bg-blue-50 border-blue-200">
            <div className="flex items-start space-x-3">
              <MessageSquare className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900">💡 How to Use</h3>
                <ul className="mt-2 space-y-1 text-sm text-gray-700">
                  <li>1. Choose a demo from the left sidebar</li>
                  <li>2. Click "Run Demo" to execute the task</li>
                  <li>3. Watch the console output in real-time</li>
                  <li>4. View the results when complete</li>
                  <li>5. Try different demos to see various capabilities!</li>
                </ul>
                <p className="mt-3 text-sm text-gray-600 italic">
                  These demos connect to the real backend and execute actual A2A tasks.
                  You're seeing the genuine system in action!
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
        </div>
      )}
    </div>
  );
};

export default LiveDemoTab;

